import { useState, useRef, useMemo, useEffect } from "react";
import { Account } from "@/types";
import { fetchAccounts } from "@/api/globalApiService";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { LuSearch } from "react-icons/lu";
import { SqButton } from "@/components/squircle-ui/button";
import { cn } from "@/lib/utils";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import { RiFileCopyLine, RiLoader4Line } from "@remixicon/react";

interface CopyToAccountsPopoverProps {
  accountId: string;
  reportId: string;
  currentPlatform: string;
  onCopy: (accountIds: string[]) => Promise<void>;
  children: React.ReactNode;
  /** When provided, use these accounts instead of fetching on open (e.g. from sidebar) */
  accounts?: Account[] | null;
  /** Loading state when accounts are provided by parent */
  isLoadingAccounts?: boolean;
}

export const CopyToAccountsPopover = ({
  accountId,
  reportId,
  currentPlatform,
  onCopy,
  children,
  accounts: accountsFromParent,
  isLoadingAccounts: isLoadingFromParent = false,
}: CopyToAccountsPopoverProps) => {
  // reportId is kept for potential future use
  void reportId;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [isCopying, setIsCopying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const accountsCacheRef = useRef<Map<string, Account[]>>(new Map());
  const pendingRequestsRef = useRef<Set<string>>(new Set());

  // Use parent-provided accounts when available
  const useParentAccounts = accountsFromParent !== undefined;
  const displayLoading = useParentAccounts ? isLoadingFromParent : isLoading;

  // Fetch accounts with same platform when popover opens (only when not using parent-provided accounts)
  useEffect(() => {
    if (useParentAccounts) return;

    if (isOpen && currentPlatform) {
      const cacheKey = `${accountId}-${currentPlatform}`;
      const cachedAccounts = accountsCacheRef.current.get(cacheKey);

      if (cachedAccounts) {
        setAccounts(cachedAccounts);
        setIsLoading(false);
      } else if (!pendingRequestsRef.current.has(cacheKey)) {
        setIsLoading(true);
        pendingRequestsRef.current.add(cacheKey);
        fetchAccounts({
          platform: currentPlatform,
          page: 1,
          search: "",
          pageSize: 1000,
        })
          .then((data) => {
            const otherAccounts = data.results.filter((acc) => acc.id !== accountId);
            accountsCacheRef.current.set(cacheKey, otherAccounts);
            setAccounts(otherAccounts);
          })
          .catch((error: unknown) => {
            console.error("Error fetching accounts:", error);
          })
          .finally(() => {
            pendingRequestsRef.current.delete(cacheKey);
            setIsLoading(false);
          });
      } else {
        setIsLoading(true);
      }
    }
  }, [useParentAccounts, isOpen, currentPlatform, accountId]);

  // Reset selections when popover closes (for both parent-provided and self-fetched)
  useEffect(() => {
    if (!isOpen) {
      setSelectedAccountIds([]);
      setSearchQuery("");
    }
  }, [isOpen]);

  // Filter accounts: exclude viewer role, then apply search
  const filteredAccounts = useMemo(() => {
    const rawList = useParentAccounts ? (accountsFromParent ?? []) : accounts;
    const list = rawList.filter((acc) => acc.role !== "viewer");
    if (!searchQuery.trim()) return list;

    const query = searchQuery.toLowerCase();
    return list.filter(
      (account) =>
        account.name.toLowerCase().includes(query) ||
        account.ad_account_id?.toLowerCase().includes(query.replace(/-/g, "")),
    );
  }, [searchQuery, useParentAccounts, accountsFromParent, accounts]);

  // Check if all filtered options are selected
  const areAllFilteredOptionsSelected = useMemo(() => {
    if (filteredAccounts.length === 0) return false;
    return filteredAccounts.every((acc) => selectedAccountIds.includes(acc.id));
  }, [filteredAccounts, selectedAccountIds]);

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccountIds((prev) => {
      const exists = prev.includes(accountId);
      return exists ? prev.filter((id) => id !== accountId) : [...prev, accountId];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredAccounts.map((acc) => acc.id);
      setSelectedAccountIds((prev) => Array.from(new Set([...prev, ...allIds])));
    } else {
      const filteredIds = new Set(filteredAccounts.map((acc) => acc.id));
      setSelectedAccountIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    }
  };

  const handleCopy = async () => {
    if (selectedAccountIds.length === 0) return;

    setIsCopying(true);
    try {
      await onCopy(selectedAccountIds);
      setIsOpen(false);
      setSelectedAccountIds([]);
    } catch (error) {
      console.error("Error copying report:", error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (open) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <div
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            timeoutRef.current = setTimeout(() => {
              setIsOpen(false);
            }, 200);
          }}
          className="w-full"
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-[6px] shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
        side="right"
        align="start"
        sideOffset={4}
        onMouseEnter={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {/* Search and Select All Header */}
        <div className="mb-[12px]">
          <div className="flex items-center gap-2 pl-[8px]">
            <Checkbox
              checked={areAllFilteredOptionsSelected}
              onCheckedChange={handleSelectAll}
              className="h-[14px] w-[14px] rounded-[4px] shrink-0"
            />
            <div className="relative flex-1">
              <LuSearch
                size={14}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
              />
              <Input
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
              />
            </div>
          </div>
        </div>

        {/* Account List */}
        <div className="relative">
          <div className="max-h-[262.5px] overflow-y-auto">
            {displayLoading ? (
              <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[12px]">
                Loading accounts...
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[12px]">
                {searchQuery ? "No results found" : "No accounts available"}
              </div>
            ) : (
              filteredAccounts.map((account) => {
                const isSelected = selectedAccountIds.includes(account.id);
                return (
                  <div
                    key={account.id}
                    onClick={() => handleAccountToggle(account.id)}
                    className={cn(
                      "h-[25px] overflow-hidden flex items-center group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray",
                    )}
                  >
                    <div className="w-full flex justify-between items-center gap-2">
                      <div className="w-full flex items-center gap-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleAccountToggle(account.id)}
                          className="h-[14px] w-[14px] rounded-[4px]"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <img
                          src={getPlatformIcon(account.platform)}
                          alt={account.platform}
                          className="h-[16px] w-[16px]"
                        />
                        <span className="flex-1 truncate text-brand-primary-new text-[12px] font-light leading-[16px]">
                          {account.name}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Copy Button */}
        {!displayLoading && (
          <div className="flex justify-end pt-[10px] pb-[6px] pr-[2px]">
            <SqButton
              size={"md"}
              variant={"default"}
              disabled={selectedAccountIds.length === 0 || isCopying}
              onClick={() => {
                void handleCopy();
              }}
              className="squircle-mask gap-[6px]"
            >
              {isCopying ? (
                <>
                  <RiLoader4Line size={16} className="animate-spin" />
                  Copying...
                </>
              ) : (
                <>
                  <RiFileCopyLine size={16} />
                  <span className="font-medium text-[11px]">Copy</span>
                </>
              )}
            </SqButton>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
